import React, { useEffect } from 'react';

import { Account } from '@baemingo/integrations-config';
import { uniqueId } from 'lodash';
import { useMutation, useQuery } from 'react-apollo';

import * as actions from 'actions';
import {
  useIntegrationsConfigsApi,
  useIntegrationsConfigsUniverse,
} from 'Components-redesign/IntegrationsConfigsUniverse';
import Titlebar from 'Components-redesign/Titlebar';
import { useDispatch, useI18n, useSelector } from 'Hooks';

import {
  SaveFieldMutation,
  SaveFieldMutationVariables,
  getAllVenues,
  getAllVenuesVariables,
} from './__queries__';
import Field from './IntegrationField';
import { SAVE_MUTATION, VENUE_QUERY } from './queries';
import { InnerWrapper, OuterWrapper } from './styles';

export type ModelType = 'CATEGORY' | 'ITEM' | 'VENUE' | 'PAYMENT_METHOD';
interface Props {
  model: ModelType;
  id: string;
}

const IntegrationConfigs: React.FunctionComponent<Props> = ({ model, id }) => {
  const { t } = useI18n();
  const companyId = useSelector(({ company }) => company);
  const dispatch = useDispatch();
  const integrationFields = useSelector((state) => state.integrationFields);
  const configurationsData = useIntegrationsConfigsUniverse();
  const api = useIntegrationsConfigsApi();

  const { appliedConfigs, accounts, configs } = configurationsData;

  const [submit, { data }] = useMutation<SaveFieldMutation, SaveFieldMutationVariables>(
    SAVE_MUTATION,
    {
      refetchQueries: ['getIntegrations'],
      awaitRefetchQueries: true,
    },
  );

  // Check if submit has been made
  useEffect(() => {
    const response = data?.submitIntegrationFormField;

    if (!response) {
      return;
    }

    if (!response.success) {
      dispatch(actions.showNotice(response.message, 4000));
    }
  }, [data]);

  // Get venues to be able to display venue name in labels
  if (!companyId) return null;
  const { data: venues } = useQuery<getAllVenues, getAllVenuesVariables>(VENUE_QUERY, {
    variables: {
      companyId,
    },
  });

  const handleSubmitOldIntegration = (field, fieldValue) => {
    const { formId, provider, venueId } = field;
    let formattedValue = fieldValue;

    if (field.type == 'NUMBER_MAP') {
      const newValue = fieldValue.reduce((accumulator, { id, value }) => {
        if (!id) {
          return accumulator;
        }
        return {
          ...accumulator,
          [id]: value,
        };
      }, {});
      formattedValue = JSON.stringify(newValue);
    }

    submit({
      variables: {
        data: {
          formId,
          value: formattedValue,
          companyId,
          relatedId: id,
          venueId,
          provider,
        },
      },
    });
  };

  const handleSubmitNewIntegration = (account, accountValue) => {
    const { key, configId, type } = account;

    const formattedValues: Account[] = [
      {
        key,
        type,
        accountNumber: accountValue,
      },
    ];

    if (configId) {
      api.putAccounts({
        configId,
        accounts: formattedValues,
      });
      dispatch(actions.showNotice(t('Integration.AccountsDataWasUpdated'), 4000));
    }
  };

  // Get the correct fields
  const fields = integrationFields.filter(
    (field) => field.model === model && (model === 'VENUE' ? field.venueId === id : true),
  );

  const accountsData = configs.map((config) => {
    // Converting the case of the model so that it can be compared with the account.type format.
    const modelName = model.charAt(0) + model.slice(1).toLowerCase();
    const existingAccount = accounts.find(
      (account) =>
        account.configId === config.id && account.type === modelName && account.key === id,
    );

    return {
      configId: config.id,
      configName: config.configName,
      provider: config.accountingProvider,
      accountNumber: existingAccount?.accountNumber ?? '',
      key: existingAccount?.key ?? id,
      type: existingAccount?.type ?? modelName,
    };
  });

  if (!fields.length && !accountsData.length) {
    return null;
  }

  // Get the saved integration value
  const getInitialValue = (values, type) => {
    if (type == 'NUMBER_MAP') {
      return values.map((data) => ({
        id: data.fieldId,
        value: data.value,
      }));
    } else {
      return values.find((data) => data.fieldId === id)?.value ?? '';
    }
  };

  // Show correct label
  const getLabelForOldIntegration = (field) => {
    const venueName =
      venues?.items?.find((venue) => venue.id == field.venueId)?.name ?? t('General.Unknown');

    return (
      field.provider + ' · ' + field.formId + (fields.length > 1 ? ' (' + venueName + ')' : '')
    );
  };

  const getLabelForNewIntegration = (account) => {
    const appliedVenues = appliedConfigs?.filter(
      (appConfig) => appConfig.configId == account?.configId,
    );
    let venueNames = '';

    if (appliedVenues.length > 1) {
      appliedVenues.forEach((appliedVenue, i) => {
        const venueName = venues?.items?.find((venue) => venue.id == appliedVenue.venueId)?.name;
        venueNames = venueNames + venueName + (i !== appliedVenues.length - 1 ? ', ' : '');
      });
    } else if (appliedVenues.length === 1) {
      const venueName = venues?.items?.find((venue) => venue.id == appliedVenues[0].venueId)?.name;
      venueNames = venueName ?? '';
    }

    return `${account?.provider?.toUpperCase()} · ${account?.configName} ${
      venueNames ? `(${venueNames})` : ''
    }`;
  };

  return (
    <OuterWrapper>
      <Titlebar title={t('General.Integrations')} zeroPadding />
      {/* Old integrations */}
      {fields.length > 0 && (
        <InnerWrapper>
          {fields.map((field) => (
            <Field
              key={uniqueId()}
              label={getLabelForOldIntegration(field)}
              type={
                field.formId === 'VenueCorporateId' && field.provider === 'SKATTEVERKET'
                  ? 'number'
                  : field.type
              }
              handleSubmit={(value) => handleSubmitOldIntegration(field, value)}
              initialValue={getInitialValue(field.values, field.type)}
            />
          ))}
        </InnerWrapper>
      )}
      {/* New integrations */}
      {accountsData.length > 0 && (
        <InnerWrapper>
          {accountsData.map((account) => (
            <Field
              key={uniqueId()}
              label={getLabelForNewIntegration(account)}
              type={'NUMBER'}
              handleSubmit={(value) => handleSubmitNewIntegration(account, value)}
              initialValue={account.accountNumber}
            />
          ))}
        </InnerWrapper>
      )}
    </OuterWrapper>
  );
};

export default IntegrationConfigs;
